import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { adminService } from "../services/admin.service";
import { loginSchema, LoginFormValues } from "../schemas/auth.schema";

interface Props {
  onLoginSuccess: (token: string) => void;
}

export const AdminLogin: React.FC<Props> = ({ onLoginSuccess }) => {
  const [errorMessage, setErrorMessage] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
  });

  const loginMutation = useMutation({
    mutationFn: adminService.login,
    onSuccess: (res) => {
      const token = res.data?.accessToken || res.accessToken;
      if (token) {
        localStorage.setItem("admin_token", token);
        onLoginSuccess(token);
      } else {
        setErrorMessage("Không nhận được token xác thực.");
      }
    },
    onError: (err: any) => {
      setErrorMessage(err.response?.data?.message || "Đăng nhập thất bại.");
    },
  });

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900 px-4">
      <div className="max-w-md w-full bg-white rounded-xl shadow-2xl p-8">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-800">
            Rice Export Admin
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Đăng nhập vào hệ thống quản trị
          </p>
        </div>

        {errorMessage && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 rounded-lg text-sm">
            {errorMessage}
          </div>
        )}

        <form
          onSubmit={handleSubmit((data) => loginMutation.mutate(data))}
          className="space-y-4"
        >
          <div>
            <label className="block text-xs font-semibold uppercase text-gray-600 mb-1">
              Email
            </label>
            <input
              type="email"
              {...register("email")}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none text-sm"
              placeholder="admin@everrice.com"
            />
            {errors.email && (
              <p className="text-red-500 text-xs mt-1">
                {errors.email.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase text-gray-600 mb-1">
              Mật khẩu
            </label>
            <input
              type="password"
              {...register("password")}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none text-sm"
              placeholder="••••••••"
            />
            {errors.password && (
              <p className="text-red-500 text-xs mt-1">
                {errors.password.message}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={loginMutation.isPending}
            className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg transition duration-200 shadow-md text-sm"
          >
            {loginMutation.isPending ? "Đang xử lý..." : "Đăng nhập"}
          </button>
        </form>
      </div>
    </div>
  );
};
